import React, { useCallback } from 'react';
import { useCrossFlow, Journeys, type TargetType } from '@atlassiansox/cross-flow-support';
import OriginTracing from '@atlassiansox/origin-tracing';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { useCrossJoinNudgeExperiment } from '@atlassian/jira-cross-join-nudges';
import { ff } from '@atlassian/jira-feature-flagging';
import { fireExposureEvent, getFeatureFlagValue } from '@atlassian/jira-growth-experiment-utils';
import {
	openSlackIntegrationPopup,
	useFetchSlackStatus,
} from '@atlassian/jira-growth-improved-integrations';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	SCREEN,
	FireUiAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { TenantContextSubscriber as TenantContextSubscriberDI } from '@atlassian/jira-tenant-context-controller';
import AtlassianSwitcher from '@atlassian/switcher';
import {
	DiscoverySurfaceContextCollector,
	DiscoverySurface,
} from './controllers/discovery-surface';
import type { Props } from './types';

const isEnrolledInUPaasRecommendationForInstance = () =>
	getFeatureFlagValue('appswitcher.ranking.jsw.xflow').value !== 'not-enrolled';

const UPAAS_RECOMMENDATIONS_FLAG = 'appswitcher.ranking.jsw.xflow';

const getUPaasRecommendationForInstance = () => ({
	[UPAAS_RECOMMENDATIONS_FLAG]:
		getFeatureFlagValue('appswitcher.ranking.jsw.xflow').value || 'not-enrolled',
});

const fireUPaasRecommendationExposureEvent = () => {
	const upaasRecommendationFeatureFlagValue = getFeatureFlagValue('appswitcher.ranking.jsw.xflow');
	fireExposureEvent(UPAAS_RECOMMENDATIONS_FLAG, upaasRecommendationFeatureFlagValue);
};

const JIRA_PRODUCT = 'jira';
const CONFLUENCE = 'CONFLUENCE';

export const AtlassianSwitcherIntegration = ({
	isOpen,
	onOpen,
	onClose,
	TenantContextSubscriber = TenantContextSubscriberDI,
	IntegrationViewRenderer,
	BeforeComponent = () => null,
}: Props) => {
	useCrossJoinNudgeExperiment();
	if (isOpen && isEnrolledInUPaasRecommendationForInstance()) {
		fireUPaasRecommendationExposureEvent();
	}
	const { isLoading, isSlackIntegrationEnabled, isSlackIntegrationConnected } =
		useFetchSlackStatus(!isOpen);
	const isSlackDiscoveryEnabled =
		!isLoading && isSlackIntegrationEnabled && !isSlackIntegrationConnected;
	const crossFlow = useCrossFlow();

	const slackDiscoveryClickHandler = useCallback(() => {
		openSlackIntegrationPopup('switcher');
		onClose();
	}, [onClose]);

	const onTriggerXFlow = useCallback(
		(targetProduct: TargetType) => {
			crossFlow.isEnabled &&
				crossFlow.api.open({
					journey: Journeys.DECIDE,
					targetProduct,
					sourceComponent: 'atlassian-switcher',
					sourceContext: 'recommendation',
					experimentalOptions: {
						isCrossSell: true,
					},
				});
			onClose();
		},
		[crossFlow, onClose],
	);

	const onDiscoverMoreClicked = useCallback(() => {
		crossFlow.isEnabled &&
			crossFlow.api.open({
				sourceComponent: 'atlassian-switcher',
				sourceContext: 'more',
				journey: Journeys.DISCOVER,
			});
		onClose();
	}, [crossFlow, onClose]);

	const origin = new OriginTracing({ product: JIRA_PRODUCT });

	const project = useProject();

	return (
		<>
			<DiscoverySurfaceContextCollector isOpen={isOpen} />
			<TenantContextSubscriber>
				{({ tenantContext }) => {
					if (!tenantContext) return null;

					const { cloudId, locale } = tenantContext;

					let isDiscoverySurfaceEnabled;

					let ffEnableOverFiftyUsers;
					let ffAllLocales;

					if (locale.startsWith('en')) {
						const val = ff('jira.frontend.atlassian-switcher.discovery-sdk');
						isDiscoverySurfaceEnabled = val;
						ffEnableOverFiftyUsers = val;
						ffAllLocales = undefined;
					} else {
						const val = ff('jira.app-recommendations.discovery-sdk.all-locale');
						isDiscoverySurfaceEnabled = val;
						ffAllLocales = val;
						ffEnableOverFiftyUsers = undefined;
					}

					const switcherProps = {
						cloudId,
						product: JIRA_PRODUCT,
						subproduct: project.data?.type,
						triggerXFlow: onTriggerXFlow,
						customizeLinks: () => ({
							// @ts-expect-error - TS7006 - Parameter 'url' implicitly has an 'any' type. | TS7006 - Parameter 'product' implicitly has an 'any' type.
							mapUrl: (url, product) => (product === CONFLUENCE ? origin.addToUrl(url) : url),
							// @ts-expect-error - TS7006 - Parameter 'product' implicitly has an 'any' type.
							getExtendedAnalyticsAttributes: (product) =>
								product === CONFLUENCE ? origin.toAnalyticsAttributes() : {},
						}),
						recommendationsFeatureFlags: { ...getUPaasRecommendationForInstance() },
						onDiscoverMoreClicked: crossFlow.isEnabled ? onDiscoverMoreClicked : null,
						joinableSitesDataProvider: null,
						isSlackDiscoveryEnabled,
						slackDiscoveryClickHandler: isSlackDiscoveryEnabled && slackDiscoveryClickHandler,
						isUFOEnabled: ff('jira.frontend.atlassian-switcher.enable-ufo'),
						discoverySurface: isDiscoverySurfaceEnabled ? DiscoverySurface : undefined,
					};
					return (
						<>
							<ContextualAnalyticsData
								sourceName="atlassianSwitcherDiscoverSection"
								sourceType={SCREEN}
								attributes={{
									discoverySurfaceCohort: { value: isDiscoverySurfaceEnabled },
								}}
							>
								{isOpen && (
									<FireScreenAnalytics
										attributes={{
											availableProducts:
												isSlackIntegrationEnabled && !isSlackIntegrationConnected
													? ['slackForAtlassianApp']
													: [],
										}}
									/>
								)}
							</ContextualAnalyticsData>
							<AnalyticsContext data={{ source: 'atlassianSwitcher' }}>
								{isOpen && (
									<FireUiAnalytics
										action="viewed"
										actionSubject="section"
										actionSubjectId="switcherDiscoveryExposure"
										attributes={{
											locale,
											featureFlags: {
												ffEnableOverFiftyUsers,
												ffAllLocales,
											},
										}}
									/>
								)}
							</AnalyticsContext>
							<BeforeComponent cloudId={cloudId} onOpen={onOpen} />
							<IntegrationViewRenderer>
								{({ IntegrationView, atlassianSwitcherCustomizationProps }) => (
									<IntegrationView
										isOpen={isOpen}
										onOpen={onOpen}
										onClose={onClose}
										icon={CrossIcon}
									>
										{/* @ts-expect-error - TS2322 - Type '{ appearance?: string | undefined; cloudId: string; product: string; subproduct: string | undefined; triggerXFlow: (targetProduct: TargetType) => void; customizeLinks: () => { ...; }; ... 9 more ...; stopSpotlight: () => Promise<...>; }' is not assignable to type 'WithAppearance'. */}
										<AtlassianSwitcher
											{...switcherProps}
											{...atlassianSwitcherCustomizationProps}
										/>
									</IntegrationView>
								)}
							</IntegrationViewRenderer>
						</>
					);
				}}
			</TenantContextSubscriber>
		</>
	);
};

export default AtlassianSwitcherIntegration;
